// src/components/dashboards/add_patient_v2/hooks/usePatientNameSuggestions.ts
// -----------------------------------------------------------------------------
// Hook backing the V2 form's Patient Name dropdown (cursor_logs/036 §28,
// behaviour B). Debounced search of EXISTING patients by name so the user can
// pick a known patient (which locks the card into "Updating <name>" mode) or
// free-type a brand-new name.
//
// Uses `GET /api/patients/list-patient-names/`, which (since cursor_logs/036
// §35) searches across EVERY environment the user can see — the same read
// boundary as the dashboard patient finder — and anonymises names. Each
// suggestion carries the patient's environments (id + name, intersected with
// the user's visible scope) so PatientCard can tell whether the picked patient
// lives in the currently-selected environment; if not, the card blocks
// creation and offers to activate that environment first (§35). The search is
// no longer scoped to a single selected env (that earlier scope hid known
// patients that lived in a non-selected but visible env).
// -----------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";
import api from "../../../../api/api";
import logger from "utils/logger";

export interface PatientNameSuggestion {
  id: number;
  /** Anonymised name when the caller lacks reveal permission (server-masked). */
  name: string;
  external_id: string;
  /** The patient's environments that fall within the user's visible scope
   *  (server-intersected). Used to detect cross-environment picks. */
  environmentIds: string[];
  /** Display names matching environmentIds (same order). */
  environmentNames: string[];
}

export interface UsePatientNameSuggestionsArgs {
  /** The current free-typed query in the name field. */
  query: string;
  /** When false the hook resets and skips searching (e.g. succeeded card). */
  enabled?: boolean;
}

export interface UsePatientNameSuggestionsResult {
  suggestions: PatientNameSuggestion[];
  /** True while a search request is in flight — drives the name field spinner. */
  loading: boolean;
}

const DEBOUNCE_MS = 250;
/** Below this length the dropdown stays quiet (the server enforces the same). */
const MIN_QUERY_LEN = 2;

export function usePatientNameSuggestions({
  query,
  enabled = true,
}: UsePatientNameSuggestionsArgs): UsePatientNameSuggestionsResult {
  const [suggestions, setSuggestions] = useState<PatientNameSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    const trimmed = query.trim();
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (trimmed.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const controller = new AbortController();
        abortRef.current = controller;
        // Scope is derived server-side from the user's visible environments
        // (§35); no environment_id param needed.
        const resp = await api.get("/patients/list-patient-names/", {
          params: { q: trimmed },
          signal: controller.signal,
        });
        const data = resp.data as {
          items: {
            patient_id: number;
            patient_name: string;
            external_id: string;
            environment_ids?: string[];
            environment_names?: string[];
          }[];
        };
        setSuggestions(
          (data.items || []).map((it) => ({
            id: it.patient_id,
            name: (it.patient_name || "").trim(),
            external_id: (it.external_id || "").trim(),
            environmentIds: (it.environment_ids || []).map((e) => String(e)),
            environmentNames: it.environment_names || [],
          })),
        );
      } catch (err: unknown) {
        const e = err as { name?: string; code?: string };
        if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
          logger.debug("[usePatientNameSuggestions] search failed", err);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, enabled]);

  return { suggestions, loading };
}

export default usePatientNameSuggestions;
